import PropTypes from 'prop-types';
import { useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	Grid,
	IconButton as DefaultIconButton,
	Menu,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

// project imports
import IconButton from 'components/@extended/IconButton';

import { Statistic } from 'antd';

// assets
import moment from 'moment';
import LynIcon from 'components/@extended/LynIcon';
import CloseIcon from '@mui/icons-material/Close';
import { BackwardOutlined, DeleteOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons';
import useChatContext from 'hooks/useChatContext';
import { socket } from 'utils/socket';

const { Countdown } = Statistic;
function ChatMessageAction({ index, messageObj, direction = 'RIGHT', date, contextMenuPosition = null, onContextMenuClose }) {
	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState(null);
	const [openModal, setOpenModal] = useState(false);
	const [chatMessage, setChatMessage] = useState(messageObj?.message?.trim() || '');

	const { editMessage, setEditMessage, setReply, context, roomId, userId, companyCode } = useChatContext();

	const handleClickSort = (event) => {
		setAnchorEl(event?.currentTarget);
	};

	const handleCloseSort = () => {
		setAnchorEl(null);
		onContextMenuClose?.();
	};

	const open = Boolean(anchorEl) || Boolean(contextMenuPosition);

	const onDeleteHandler = (message) => {
		// console.log("message", message);
		if (message?.id) {
			const payload = {
				...message,
				context,
				userId,
				roomId,
				companyCode,
				type: 'DELETE',
			};

			socket.emit('message', payload);
		}
		setAnchorEl(null);
		onContextMenuClose?.();
	};

	const onEditHandler = (msg) => {
		// console.log("msg", msg);
		setEditMessage(msg);
		setChatMessage(msg?.message?.trim());
		setAnchorEl(null);
		setOpenModal(true);
	};

	const onChangeHandler = (e) => {
		setChatMessage(e.target.value.length <= 1 ? e.target.value.trim() : e.target.value);
	};

	const onSendHandler = (message) => {
		const validMessage = chatMessage.trim();

		if (validMessage === '') {
			// do something when user hit enter without passing any message in chat
		} else {
			// ------------------ Send message via Socket API ------------------
			if (message?.id) {
				if (editMessage?.message !== chatMessage) {
					const updateMessage = {
						...message,
						message: chatMessage,
						context,
						userId,
						roomId,
						companyCode,
						type: 'UPDATE',
					};

					socket.emit('message', updateMessage);
				}

				setChatMessage('');
				setOpenModal(false);
			}
		}
	};

	const onReplayHandler = (message) => {
		setReply(message);
		setAnchorEl(null);
		onContextMenuClose?.();
	};

	const handleKeyDown = (event) => {
		if (event.keyCode === 13 && event.shiftKey) {
			setChatMessage(chatMessage + '\n');
			event.preventDefault();
			return;
		}

		if (event?.key !== 'Enter') {
			return;
		}

		if (event?.key === 'Enter') {
			onSendHandler(messageObj);
			event.preventDefault();
			return;
		}
	};

	const onFinish = () => {
		setChatMessage('');
		setOpenModal(false);
	};

	return (
		<>
			<IconButton
				id={`chat-action-button-${index}`}
				aria-controls={open ? `chat-action-menu-${index}` : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				onClick={handleClickSort}
				size="small"
				color="secondary"
			>
				<MoreOutlined />
			</IconButton>
			<Menu
				id={`chat-action-menu-${index}`}
				anchorEl={anchorEl}
				anchorReference={contextMenuPosition ? 'anchorPosition' : 'anchorEl'}
				anchorPosition={
					contextMenuPosition
						? {
								top: contextMenuPosition.top,
								left: contextMenuPosition.left,
						  }
						: undefined
				}
				keepMounted
				open={open}
				onClose={handleCloseSort}
				anchorOrigin={{
					...(direction === 'RIGHT' ? { vertical: 'top', horizontal: 'left' } : { vertical: 'top', horizontal: 'right' }),
				}}
				transformOrigin={{
					...(direction === 'RIGHT' ? { vertical: 'top', horizontal: 'right' } : { vertical: 'top', horizontal: 'left' }),
				}}
				MenuListProps={{
					'aria-labelledby': `chat-action-button-${index}`,
				}}
				sx={{
					p: 0,
					'& .MuiMenu-list': {
						p: 0,
					},
				}}
			>
				{direction === 'RIGHT' && diff_minutes(date) <= 30 && messageObj?.message !== '' && (
					<MenuItem onClick={() => onEditHandler(messageObj)}>
						<EditOutlined style={{ paddingRight: 8 }} />
						<Typography>Edit</Typography>
					</MenuItem>
				)}
				<MenuItem onClick={() => onReplayHandler(messageObj)}>
					<BackwardOutlined style={{ paddingRight: 8 }} />
					<Typography>Reply</Typography>
				</MenuItem>
				{direction === 'RIGHT' && diff_minutes(date) <= 30 && (
					<MenuItem onClick={() => onDeleteHandler(messageObj)}>
						<DeleteOutlined style={{ paddingRight: 8, paddingLeft: 0 }} />
						<Typography>Delete</Typography>
					</MenuItem>
				)}
			</Menu>

			<Dialog
				fullWidth={true}
				maxWidth="xs"
				open={openModal}
				onClose={() => setOpenModal(false)}
				aria-labelledby="edit-message-dialog"
				aria-describedby="edit-message-dialog"
			>
				<Stack direction="row" alignItems="center" sx={{ py: 1.5, px: 1 }} spacing={0.5}>
					<Typography variant="subtitle1">Edit Message: </Typography>
					<Typography variant="small" sx={{ lineHeight: 0 }}>
						<Countdown
							style={{ display: 'inline-block', lineHeight: 'line-height: 21.98px' }}
							valueStyle={{ fontSize: '12px', fontWight: '600' }}
							value={deadline(date, 30)}
							format="mm [mins] ss [Secs]"
							onFinish={onFinish}
						/>{' '}
						left
					</Typography>
				</Stack>
				<DefaultIconButton
					aria-label="close"
					onClick={() => setOpenModal(false)}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
					}}
				>
					<CloseIcon />
				</DefaultIconButton>
				<DialogContent dividers>
					<Grid item xs={12} key={index}>
						<Stack direction="row" alignItems="flex-start" spacing={1.25}>
							<Grid container spacing={0.5} justifyContent="flex-end">
								<Grid item xs={2} md={3} xl={4} />

								<Grid item xs={10} md={9} xl={8}>
									<Stack direction="row" justifyContent="flex-end" alignItems="flex-start">
										<Box
											sx={{
												display: 'inline-block',
												wordWrap: 'break-word',
												whiteSpace: 'pre-wrap',
												float: 'right',
												bgcolor: '#4169E1',
												color: 'common.white',
												borderRadius: '10px 10px 2px 10px',
												p: '8px 10px 9px 20px',
											}}
										>
											<Stack direction="row" alignItems="center" justifyContent="start">
												<Typography variant="chat" sx={{ overflowWrap: 'anywhere' }}>
													{editMessage?.message}
												</Typography>
												<Typography variant="date" sx={{ visibility: 'hidden', pl: 1 }}>
													{date && moment(date).format('LT')}
												</Typography>
											</Stack>
											<Box
												sx={{
													display: 'inline-block',
													float: 'right',
													lineHeight: '10px',
													marginBottom: '-8px',
													marginRight: '-5px',
													marginTop: '-10px',
												}}
											>
												<Typography variant="date" component="span" align="right" sx={{ color: 'white' }}>
													{date && moment(date).format('LT')}
												</Typography>
											</Box>
										</Box>
									</Stack>
								</Grid>
							</Grid>
						</Stack>
					</Grid>
				</DialogContent>
				<DialogActions sx={{ p: 0 }}>
					<Box variant="div" sx={{ p: 1, bgcolor: '#f2f2f2', width: '100%' }}>
						<Box variant="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<Box variant="div" sx={{ width: '100%', bgcolor: theme.palette.background.light, mx: '10px', p: 0, borderRadius: '9px' }}>
								<TextField
									fullWidth
									multiline
									maxRows={3}
									onChange={onChangeHandler}
									onKeyDown={handleKeyDown}
									placeholder="Your Message..."
									value={chatMessage}
									variant="standard"
									InputProps={{ disableUnderline: true }}
									sx={{
										p: 1.2,
										// "& .MuiInput-root:before": { borderBottomColor: theme.palette.divider },
									}}
								/>
							</Box>

							<Box
								variant="div"
								sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px' }}
							>
								<IconButton
									variant="contained"
									onClick={() => onSendHandler(messageObj)}
									sx={{ bgcolor: '#4169E1', color: 'lynTheme.blue', borderRadius: '9px', '&:hover': { bgcolor: '#4169E1' } }}
								>
									<LynIcon folder="icons" fileName="send" format="svg" sx={{ width: '18px' }} />
								</IconButton>
							</Box>
						</Box>
					</Box>
				</DialogActions>
			</Dialog>
		</>
	);
}

ChatMessageAction.propTypes = {
	index: PropTypes.number,
	contextMenuPosition: PropTypes.shape({
		top: PropTypes.number,
		left: PropTypes.number,
	}),
	onContextMenuClose: PropTypes.func,
};

export default ChatMessageAction;

function diff_minutes(dt1) {
	let t1 = new Date(dt1);
	let t2 = new Date();

	var diff = (t2.getTime() - t1.getTime()) / 1000;
	diff /= 60;

	return Math.abs(Math.round(diff));
}

function deadline(creationDate, allowedTime) {
	let allowedDateInMileSecond = allowedTime * 60 * 1000;
	let creationDateInMileSecond = new Date(creationDate).getTime();

	return creationDateInMileSecond + allowedDateInMileSecond;
}
